//frontend\src\components\HourlyStatisticsTable.js

import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper, Box, CircularProgress, Typography,
  Table, TableHead, TableBody, TableRow, TableCell, Button
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh'; // ← Correct default import
import { fetchHourlyStats } from '../services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const CATEGORY_LABELS = [
  { key: 'giant', label: 'XL', color: '#1976d2' },
  { key: 'wrapping', label: 'WP', color: '#ab47bc' },
  { key: 'giftonly', label: 'GiftOnly', color: '#43a047' },
  { key: 'cardgift', label: 'Cards+Gifts', color: '#ffa000' },
];

const STAT_KEYS = [
  { key: 'received', label: 'Received', color: '#42a5f5' },
  { key: 'dispatched', label: 'Done', color: '#66bb6a' },
  { key: 'todo', label: 'To Do', color: '#ffa726' },
];

// Helper to compute min/max for each stat across all categories and hours
function getStatMinMax(data) {
  const minMax = {};
  for (const stat of STAT_KEYS) {
    let min = Infinity, max = -Infinity;
    for (const cat of CATEGORY_LABELS) {
      for (const row of data) {
        const val = row[`${cat.key}_${stat.key}`] ?? 0;
        if (val < min) min = val;
        if (val > max) max = val;
      }
    }
    minMax[stat.key] = { min, max };
  }
  return minMax;
}

// Color scale: blends white to the base color based on value
function getHeatColor(value, min, max, baseColor) {
  if (max === min) return '#fff'; // no variance
  // Calculate strength: 0 (min) -> 1 (max)
  let pct = (value - min) / (max - min);
  pct = Math.sqrt(pct); // make scale more visible for midrange values
  // Blend base color with white
  // Assumes baseColor is hex like "#42a5f5"
  const rgb = [
    parseInt(baseColor.slice(1, 3), 16),
    parseInt(baseColor.slice(3, 5), 16),
    parseInt(baseColor.slice(5, 7), 16)
  ];
  const blend = (ch) => Math.round(255 - (255 - ch) * pct);
  return `rgb(${blend(rgb[0])},${blend(rgb[1])},${blend(rgb[2])})`;
}

export default function HourlyStatisticsTable() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [minMax, setMinMax] = useState(null);

  useEffect(() => {
    fetchHourlyStats()
      .then((data) => {
        // Show raw hour part from DB string
        const processed = data.map(row => ({
          ...row,
          hour: typeof row.hour === 'string'
            ? row.hour.slice(11, 13) + ':00'
            : row.hour,
        }));
        setRows(processed);
        setMinMax(getStatMinMax(processed));
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetchHourlyStats()
      .then((data) => {
        const processed = data.map(row => ({
          ...row,
          hour: typeof row.hour === 'string'
            ? row.hour.slice(11, 13) + ':00'
            : row.hour,
        }));
        setRows(processed);
        setMinMax(getStatMinMax(processed));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Paper sx={{ width: '100%', p: 3, mb: 4 }}>
        
      <Box sx={{ display: 'flex', alignItems: 'center', pb: 2, gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Last 24 Hours (Heatmap View)
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh
        </Button>
        {loading && (
          <CircularProgress size={22} sx={{ ml: 1 }} />
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          <b>Description:</b> This table shows hourly statistics for order flow in the last 24 hours. Each hour records:
          <ul style={{ marginTop: 4, marginBottom: 4 }}>
            <li><b>Received</b> — Number of new orders created during the hour.</li>
            <li><b>Done</b> — Number of orders marked as Dispatched or Cancelled during the hour.</li>
            <li><b>To Do</b> — Snapshot of outstanding (undispatched/uncancelled) orders at the end of each hour. This includes all orders created before the hour, minus those already dispatched/cancelled by then.</li>
          </ul>
        </Typography>
      </Box>
      {loading ? (
        <Box sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: 16, background: '#f5f5f5' }}>Hour</TableCell>
                {CATEGORY_LABELS.map(cat => (
                  <TableCell
                    key={cat.key}
                    colSpan={3}
                    align="center"
                    sx={{ fontWeight: 700, background: cat.color, color: '#fff', fontSize: 15 }}
                  >
                    {cat.label}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {CATEGORY_LABELS.map(cat =>
                  STAT_KEYS.map(stat =>
                    <TableCell
                      key={cat.key + '_' + stat.key}
                      align="center"
                      sx={{
                        fontWeight: 600,
                        background: '#f5f5f5',
                        minWidth: 72
                      }}
                    >
                      {stat.label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.hour + '_' + idx}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.hour}</TableCell>
                  {CATEGORY_LABELS.map(cat =>
                    STAT_KEYS.map(stat => {
                      const value = row[`${cat.key}_${stat.key}`] ?? 0;
                      let bgColor = '#fff';
                      if (minMax) {
                        const { min, max } = minMax[stat.key];
                        bgColor = getHeatColor(value, min, max, stat.color);
                      }
                      return (
                        <TableCell
                          key={cat.key + '_' + stat.key}
                          align="center"
                          sx={{
                            background: bgColor,
                            fontWeight: value > 0 ? 700 : 500,
                            transition: 'background 0.3s',
                            borderRight: '1px solid #f0f0f0'
                          }}
                        >
                          {value}
                        </TableCell>
                      );
                    })
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}